<script lang="ts">
  import { browser } from "$app/environment";
  import { onMount } from "svelte";
  import { pageMainColor, headerVariant } from "$stores";
  import { setAppContext } from "$lib/utils/contexts";
  import { parseUrl } from "$lib/utils/helpers";
  import { renderRichText } from "@storyblok/svelte";
  import { userDetails } from "$lib/consumer/stores/userDetails";
  import { deviceDetails } from "$lib/consumer/stores/deviceDetails";
  import GetDeliveryForm from "$lib/consumer/GetDeliveryForm.svelte";
  import DeliveryCount from "$lib/consumer/DeliveryCount.svelte";
  import EmailForm from "$lib/consumer/EmailForm.svelte";
  import gsap from "gsap";

  // Get the block data
  export let blok;

  // Set the page type
  setAppContext("pageType", "check-availability-page");

  // Set the header bar to transparent
  headerVariant.set("transparent-light");

  // Unset the background color (removes bottom padding from the main element)
  pageMainColor.set("none");

  // Declare references for the animation elements
  let droneCanvas: HTMLCanvasElement;
  let droneFlyingAnimation: gsap.core.Tween;
  let unavailableMainHeading: HTMLElement;
  let unavailableDroid: HTMLElement;

  // Initialize the component state
  let loadingScreenDuration = 3;
  let loadingScreenTimeout: number;
  let isDeliveryAvailable: boolean | undefined = undefined;
  let screenUrlParameter: string | null = null;

  // Get the device details
  $: ({ deviceType, isMobile } = $deviceDetails);

  // Get the user address from the user details store
  $: ({ street, city, state, latitude, longitude } = $userDetails);

  // Set the users full address for later
  $: fullAddress = `${street}<br />${city}, ${state}`;

  // Check if the check availability form has been submitted
  $: formSubmitted = $userDetails.submitted;

  // Define the screens
  const screen = {
    SEARCH: "search",
    LOADING: "loading",
    AVAILABLE: "available",
    UNAVAILABLE: "unavailable",
  };

  // Set the initial screen
  let currentScreen = screen.SEARCH;

  $: {
    // Set the current screen based on the component state
    if (!screenUrlParameter) {
      if (!formSubmitted || !latitude || !longitude) {
        // Search
        currentScreen = screen.SEARCH;
      } else if (isDeliveryAvailable === undefined) {
        // Loading (displays while checking availability)
        currentScreen = screen.LOADING;
      } else if (isDeliveryAvailable === true) {
        // Available
        currentScreen = screen.AVAILABLE;
      } else if (isDeliveryAvailable === false) {
        // Unavailable
        currentScreen = screen.UNAVAILABLE;
      }
    }

    // Set the current screen to the screen query parameter (for debugging)
    if (screenUrlParameter) {
      if (screenUrlParameter === "loading") {
        // Loading
        currentScreen = screen.LOADING;
        street = "1834 Rosemont St";
        city = "Mesquite";
        state = "TX";
      } else if (screenUrlParameter === "available") {
        // Available
        currentScreen = screen.AVAILABLE;
      } else if (screenUrlParameter === "unavailable") {
        // Unavailable
        currentScreen = screen.UNAVAILABLE;
      }
    }
  }

  // Trigger the GTM page view events for each screen
  $: if (browser && window.dataLayer) {
    if (currentScreen === screen.LOADING) {
      window.dataLayer.push({
        event: "page_delivery_loading",
      } as any);
    } else if (currentScreen === screen.AVAILABLE) {
      window.dataLayer.push({
        event: "page_delivery_available",
      } as any);
    } else if (currentScreen === screen.UNAVAILABLE) {
      window.dataLayer.push({
        event: "page_delivery_unavailable",
      } as any);
    }
  }

  // Reset the scroll position when the current screen changes (but only on the client side)
  $: if (currentScreen && typeof window !== "undefined") {
    resetScrollPosition();
  }

  // Play the drone hovering animation when the loading screen is shown
  $: if (droneCanvas && currentScreen === screen.LOADING) {
    startDroneFlyingAnimation();
  }

  // Play the droid descending animation when the unavailable screen is shown
  $: if (currentScreen === screen.UNAVAILABLE) {
    setTimeout(startDroidDescendingAnimation, 100);
  }

  // Start the availability check
  $: if (formSubmitted && latitude && longitude) {
    checkDeliveryAvailability(latitude, longitude);
  }

  /**
   * Checks if delivery is available at the provided coordinates
   * - Query the serviceable areas endpoint to determine availability
   * - Show the loading screen for the specified duration
   * - Show the appropriate results screen (available or unavailable)
   *
   * @param latitude
   * @param longitude
   */
  async function checkDeliveryAvailability(latitude: number, longitude: number) {
    // Reset the availability state
    isDeliveryAvailable = undefined;

    // Query the servicable areas endpoint
    const apiUrl = `https://afr1-production.platform.flyzipline.com/api/customer-backend/public/serviceable_areas/?lat=${latitude}&lon=${longitude}`;
    let apiResult: boolean | undefined;

    const apiRequestPromise = fetch(apiUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`API responded with status ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        // If platform 2 then delivery is available
        apiResult = data.platform === 2 ? true : false;
      })
      .catch((error) => {
        console.error("Error checking availability:", error);
        apiResult = false;
      });

    // Create a timer to ensure the loading screen is shown for at least the specified duration
    const timerPromise = new Promise((resolve) => {
      loadingScreenTimeout = window.setTimeout(() => {
        killDroneFlyingAnimation();
        resolve(null);
      }, loadingScreenDuration * 1000);
    });

    // Wait for both promises to resolve
    await Promise.all([apiRequestPromise, timerPromise]);

    // Set the delivery available state, which immediately shows the appropriate results screen
    isDeliveryAvailable = apiResult;
  }

  /**
   * Starts the drone flying animation on the loading screen
   */
  function startDroneFlyingAnimation() {
    if (!droneCanvas) return;

    const context = droneCanvas.getContext("2d");
    if (!context) return;

    // Set the canvas size based on the viewport dimensions
    droneCanvas.width = isMobile ? 400 : 800;
    droneCanvas.height = isMobile ? 400 : 800;

    // Set the sequence and group frames
    let sequence = { frame: 0 };
    let frames = { start: 1, end: 70 };

    // Preload the appropriate drone images (mobile or desktop)
    const images: HTMLImageElement[] = [];

    // Finesse device type
    deviceType = deviceType === "tablet" || deviceType === "desktop" ? "desktop" : "mobile";

    // Function to load an image at a specific index
    const loadImage = (index: number) => {
      const image = new Image();
      image.src = `/images/check-availability/loading/drone-sequence/${deviceType}/${(index + 1)
        .toString()
        .padStart(4, "0")}.png`;

      // If this is the first image, display it immediately when loaded
      if (index === 0) {
        image.onload = () => {
          if (context) {
            context.clearRect(0, 0, droneCanvas.width, droneCanvas.height);
            context.drawImage(image, 0, 0);
          }
        };
      }

      return image;
    };

    // Load the first image immediately
    images[0] = loadImage(0);

    // Load the rest of the images
    for (let i = 1; i <= frames.end; i++) {
      images[i] = loadImage(i);
    }

    // Animate the drone image sequence
    droneFlyingAnimation = gsap.to(sequence, {
      frame: frames.end,
      snap: "frame",
      ease: "none",
      duration: 2.5,
      repeat: -1,
      onUpdate: () => {
        // Check if the current image is loaded
        if (images[sequence.frame]?.complete) {
          // Clear the previous frame and draw a new one
          if (droneCanvas && context) {
            context?.clearRect(0, 0, droneCanvas.width, droneCanvas.height);
            context?.drawImage(images[sequence.frame], 0, 0);
          }
        }
      },
      onRepeat: () => {
        sequence.frame = 0;
      },
    });
  }

  /**
   * Kills the drone flying animation on the loading screen
   */
  function killDroneFlyingAnimation() {
    droneFlyingAnimation?.kill();
  }

  /**
   * Animates the droid position so that it descends below the heading on the unavailable screen
   */
  function startDroidDescendingAnimation() {
    if (unavailableDroid && unavailableMainHeading) {
      const headingHeight = unavailableMainHeading.offsetHeight;

      // Position the droid below the heading with some spacing
      const offset = isMobile ? 50 : 70;
      const topPosition = headingHeight + offset;
      unavailableDroid.style.top = `${topPosition}px`;
    }
  }

  /**
   * Preloads images for all screens
   */
  function preloadScreenImages() {
    let imagesToPreload: string[];
    imagesToPreload = [
      "/images/check-availability/unavailable/droid-midair.webp",
      "/images/check-availability/available/zip-delivery.webp",
      "/images/check-availability/available/background-clouds.webp",
      "/images/check-availability/available/foreground-clouds.webp",
    ];

    const fragment = document.createDocumentFragment();
    imagesToPreload.forEach((src) => {
      const link = document.createElement("link");
      link.rel = "preload";
      link.href = src;
      link.as = "image";
      fragment.appendChild(link);
    });

    document.head.appendChild(fragment);
  }

  /**
   * Resets the scroll position to the top of the page
   */
  function resetScrollPosition() {
    window.scrollTo({ top: 0, behavior: "instant" });
  }

  // Mount the component
  onMount(() => {
    // Reset the scroll position
    resetScrollPosition();

    // Preload all images
    preloadScreenImages();

    // Get the screen URL parameter
    const urlParameters = new URLSearchParams(window.location.search);
    screenUrlParameter = urlParameters.get("screen");

    // Update the droid descending animation position on resize (on the unavailable screen)
    window.addEventListener("resize", startDroidDescendingAnimation);

    // Unmount the component
    return () => {
      // Reset the user details
      userDetails.reset();

      // Kill the drone flying animation
      killDroneFlyingAnimation();

      // Clear the loading screen timeout
      if (loadingScreenTimeout) {
        clearTimeout(loadingScreenTimeout);
      }

      // Remove the resize listener for the droid descending animation
      window.removeEventListener("resize", startDroidDescendingAnimation);
    };
  });
</script>

<div
  class="check-availability-section"
  style="--loading-screen-duration: {loadingScreenDuration}s;"
>
  <!-- Search Screen -->
  {#if currentScreen === screen.SEARCH}
    <section class="search-screen">
      <div class="clouds"></div>
      <div class="content-wrapper section-header-width mx-auto">
        <h2 class="main-subheading">{blok.searchHeading}</h2>
        <GetDeliveryForm standalone={true} visible={true} />
      </div>
    </section>
  {/if}

  <!-- Loading Screen -->
  {#if currentScreen === screen.LOADING}
    <section class="loading-screen">
      <div class="clouds"></div>
      <div class="content-wrapper section-header-width mx-auto">
        <div class="progressbar">
          <div class="progressbar-inner"></div>
        </div>
        <div class="content">
          <p class="loading-text">{blok.loadingHeading}</p>
          <p class="loading-text loading-address">{@html fullAddress}</p>
        </div>
      </div>

      <canvas id="drone-canvas" bind:this={droneCanvas} />
    </section>
  {/if}

  <!-- Available Screen -->
  {#if currentScreen === screen.AVAILABLE}
    <section class="available-screen">
      <div class="content-wrapper section-header-width mx-auto">
        <div class="content-column-text">
          <h1 class="main-heading type:h2">{blok.availableHeading}</h1>
          <h2 class="main-subheading">
            {blok.availableSubheading}
          </h2>
          <div class="download-buttons">
            <a
              href={parseUrl(blok.appleAppStoreLink)}
              class="download-button-ios-app-store scale-up-on-hover"
              on:click={() => {
                // Trigger the GTM click event
                if (browser && window.dataLayer) {
                  window.dataLayer.push({
                    event: "ios_app_store_click",
                  });
                }
              }}
            >
              <img
                src="/images/check-availability/available/button-app-store.svg"
                alt="Download on the App Store"
              />
            </a>
            <a
              href={parseUrl(blok.googlePlayStoreLink)}
              class="download-button-google-play-store scale-up-on-hover"
              on:click={() => {
                // Trigger the GTM click event
                if (browser && window.dataLayer) {
                  window.dataLayer.push({
                    event: "google_play_store_click",
                  });
                }
              }}
            >
              <img
                src="/images/check-availability/available/button-google-play.svg"
                alt="Get it on Google Play"
              />
            </a>
          </div>

          {#if blok.qrCodeImage.filename}
            <h2 class="main-subheading">
              {blok.qrCodeHeading}
            </h2>
            <img class="qr-code-image" src={blok.qrCodeImage.filename} alt="App Download QR Code" />
          {/if}
        </div>

        <div class="content-column-image">
          <img
            class="available-image"
            src="/images/check-availability/available/zip-delivery.webp"
            alt="A Zip deliverying a package"
          />
        </div>
      </div>
      <div class="foreground-clouds"></div>
      <div class="background-clouds"></div>
    </section>
  {/if}

  <!--  Unavailable Screen -->
  {#if currentScreen === screen.UNAVAILABLE}
    <section class="unavailable-screen">
      <div class="content-wrapper section-header-width mx-auto">
        <h1 class="main-heading type:h2" bind:this={unavailableMainHeading}>
          {blok.unavailableHeading}
        </h1>
        <p class="form-heading">
          {blok.unavailableFormHeading}
        </p>
        <EmailForm {blok} />

        <div class="delivery-count-wrapper">
          <h2 class="delivery-count-heading">{blok.unavailableDeliveryCountHeading}</h2>
          <span class="delivery-count-number"><DeliveryCount /></span>
          <p class="delivery-count-subheading">{blok.unavailableDeliveryCountSubheading}</p>
        </div>

        {#if !blok.hideLearnMorePosts}
          <div class="learn-more-posts">
            {#each blok.unavailableLearnMorePosts as item}
              <div class="learn-more-post">
                <img
                  class="learn-more-post-image"
                  src="{item.image.filename}/m/1160x840"
                  alt={item.image.alt}
                />
                <p class="learn-more-post-text">
                  {@html renderRichText(item.content)}
                </p>
              </div>
            {/each}
          </div>
        {/if}
      </div>

      <div class="unavailable-footer">
        <div class="unavailabe-footer-content section-header-width mx-auto">
          <h2 class="body-heading">{blok.unavailableFooterHeading}</h2>
          <p class="form-heading footer-form-heading">
            {blok.unavailableFooterFormHeading}
          </p>
          <EmailForm {blok} />
        </div>
      </div>
      <img
        class="unavailable-droid"
        src="/images/check-availability/unavailable/droid-midair.webp"
        alt="A Droid descending from the sky"
        bind:this={unavailableDroid}
      />
      <div class="clouds"></div>
    </section>
  {/if}
</div>

<style lang="postcss">
  /**
   * Drone animation
   */

  #drone-canvas {
    height: 300px;
    width: 300px;
    margin: 0 auto;

    @media (min-width: 768px) {
      height: 500px;
      width: 500px;
    }
  }

  /**
   * Common
   */

  .main-heading {
    font-size: 50px;
    font-family: var(--font-secondary);
    font-weight: var(--font-weight-semibold);
    line-height: 1;
    color: #fff;
    text-align: center;

    @screen md {
      font-size: 72px;
    }

    @screen lg {
      font-size: 96px;
    }
  }

  .main-subheading {
    color: #fff;
    text-align: center;
    font-weight: 300;
  }

  .body-heading {
    font-size: 48px;
    font-family: var(--font-secondary);
    font-weight: var(--font-weight-semibold);
    line-height: 1;
    color: #fff;
    text-align: center;

    @screen md {
      font-size: 66px;
    }

    @screen lg {
      font-size: 88px;
    }
  }

  /**
   * Search Screen
   */

  .search-screen {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    .content-wrapper {
      margin-top: -6rem;

      @screen lg {
        margin-top: -10rem;
      }
    }

    .main-subheading {
      margin-bottom: 1.5rem;
      font-size: 20px;
    }
  }

  /**
   * Search and Loading Screens
   */

  .search-screen,
  .loading-screen {
    position: relative;
    margin-top: -68px;
    min-height: 100vh;
    pointer-events: auto;
    z-index: 1;
    overflow: hidden;
    background: linear-gradient(180deg, #f3aa44 0%, #dc4193 100%);

    .content-wrapper {
      padding-top: 4rem;

      @screen md {
        padding-top: 6rem;
      }
    }

    .clouds {
      position: absolute;
      width: 100%;
      height: 100%;
      background-image: url("/images/clouds-p1-back.webp");
      background-repeat: no-repeat;
      background-position: center bottom;
      background-size: cover;
      opacity: 0.4;
      z-index: 2;
      pointer-events: none;
      animation: cloud-scale calc(var(--loading-screen-duration) + 4s) forwards;
    }

    @keyframes cloud-scale {
      0% {
        transform: scale(1);
      }
      100% {
        transform: scale(2);
      }
    }
  }

  /**
   * Loading Screen
   */

  .loading-screen {
    .loading-text {
      color: #fff;
      font-size: 16px;
      font-weight: 300;
      line-height: 1.4;

      @screen md {
        font-size: 18px;
      }
    }

    .loading-address {
      opacity: 50%;
      animation: fade-in 1s forwards var(--loading-screen-duration);
    }

    @keyframes fade-in {
      0% {
        opacity: 50%;
      }
      100% {
        opacity: 100%;
      }
    }

    .progressbar {
      width: 100%;
      height: 2px;
      background-color: rgba(255, 255, 255, 0.3);
      border-radius: 2px;
      margin: 1rem auto 1rem auto;
      overflow: hidden;

      @screen md {
        margin-top: 0.5rem;
        margin-bottom: 1.25rem;
      }
    }

    .progressbar-inner {
      height: 100%;
      width: 100%;
      background-color: white;
      transform-origin: left;
      animation: scrollbar-progress var(--loading-screen-duration) forwards;
    }

    @keyframes scrollbar-progress {
      0% {
        transform: scaleX(0);
      }
      20% {
        transform: scaleX(0.3);
        animation-timing-function: ease-out;
      }
      50% {
        transform: scaleX(0.6);
        animation-timing-function: ease-in-out;
      }
      80% {
        transform: scaleX(0.8);
        animation-timing-function: ease-in-out;
      }
      95% {
        transform: scaleX(0.95);
        animation-timing-function: ease-in;
      }
      100% {
        transform: scaleX(1);
      }
    }

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(180deg, #dc4193 0%, #2d77cb 60%);
      opacity: 0;
      z-index: -1;
      animation: fade-gradient calc(var(--loading-screen-duration) + 3s) forwards;

      @keyframes fade-gradient {
        0% {
          opacity: 0;
        }
        100% {
          opacity: 1;
        }
      }
    }
  }

  /**
   * Available Screen
   */

  .available-screen {
    position: relative;
    display: flex;
    flex-direction: column;
    margin-top: -68px;
    min-height: 100vh;
    background-color: #6fb5e7;
    overflow: hidden;

    .content-wrapper {
      position: relative;
      display: flex;
      flex-direction: column;
      gap: 2rem;
      padding-top: 6rem;
      padding-bottom: 12rem;
      z-index: 2;

      @screen lg {
        flex-direction: row;
        gap: 4rem;
        padding-top: 6rem;
        padding-bottom: 3rem;
      }
    }

    .content-column-text {
      @screen lg {
        width: 50%;
        margin-top: 8rem;
      }
    }

    .content-column-image {
      display: flex;
      justify-content: center;

      @screen lg {
        width: 50%;
        justify-content: flex-end;
      }

      img {
        border-radius: 8px;
      }
    }

    .available-image {
      transform: translateY(20px);
      opacity: 0.8;
      transition:
        transform 1s ease-out,
        opacity 1s ease-out;
      will-change: transform, opacity;
      animation: slide-up-fade-in 1.5s ease forwards;
    }

    @keyframes slide-up-fade-in {
      0% {
        transform: translateY(20px);
        opacity: 0.8;
      }
      100% {
        transform: translateY(0);
        opacity: 1;
      }
    }

    .main-heading {
      font-size: 40px;

      @screen lg {
        font-size: 80px;
        text-align: left;
      }
    }

    .main-subheading {
      margin-top: 2rem;
      text-shadow: 0 1px 2px #478aba;

      @screen lg {
        text-align: left;
      }
    }

    .download-buttons {
      display: flex;
      justify-content: center;
      gap: 1rem;
      margin-top: 1.5rem;

      @screen lg {
        gap: 2rem;
        justify-content: left;
        margin-top: 1.5rem;
      }

      img {
        height: 50px;

        @screen lg {
          height: 60px;
        }
      }
    }

    .qr-code-image {
      margin-top: 1rem;
      margin-left: auto;
      margin-right: auto;
      width: 85px;
      height: 85px;

      @screen lg {
        margin-top: 1.25rem;
        margin-left: 0;
        margin-right: 0;
      }
    }

    .background-clouds {
      width: 1244px;
      height: 592px;
      position: absolute;
      top: 20vh;
      left: 80%;
      background-image: url("/images/check-availability/available/background-clouds.webp");
      background-repeat: no-repeat;
      background-position: center center;
      background-size: contain;
      opacity: 0.5;
      z-index: 1;
      animation: background-clouds-mobile 2s ease forwards;

      @screen lg {
        top: 2vh;
        left: 10%;
        animation: background-clouds-tablet 2s ease forwards;
      }

      @media (min-width: 1440px) {
        left: 55%;
        animation: background-clouds-desktop 2s ease forwards;
      }
    }

    @keyframes background-clouds-mobile {
      0% {
        transform: translateX(-50%) scale(0.8);
      }
      100% {
        opacity: 1;
        transform: translateX(-50%) scale(1);
      }
    }

    @keyframes background-clouds-desktop {
      0% {
        top: 8vh;
        transform: translateX(-50%) scale(1);
      }
      100% {
        top: 3vh;
        opacity: 1;
        transform: translateX(-50%) scale(1.2);
      }
    }

    .foreground-clouds {
      width: 1152px;
      height: 386px;
      position: absolute;
      left: 50%;
      bottom: 0;
      background-image: url("/images/check-availability/available/foreground-clouds.webp");
      background-repeat: no-repeat;
      background-position: center center;
      background-size: contain;
      opacity: 0.5;
      z-index: 3;
      pointer-events: none;
      animation: foreground-clouds-mobile 4s ease forwards;

      @keyframes foreground-clouds-mobile {
        0% {
          transform: translateX(-50%) scale(1);
        }
        100% {
          transform: translateX(-50%) scale(1.2);
        }
      }

      @screen lg {
        top: 100vh;
        animation: foreground-clouds-desktop 4s ease forwards;
      }

      @media (min-width: 1440px) {
        top: 82vh;
      }

      @media (min-width: 1680px) {
        top: 75vh;
      }

      @media (min-width: 1920px) {
        top: 70vh;
      }

      @media (min-width: 2400px) {
        top: 65vh;
        animation: foreground-clouds-wide-desktop 4s ease forwards;
      }
    }

    @keyframes foreground-clouds-desktop {
      0% {
        transform: translateX(-55%) scale(1.2);
      }
      100% {
        transform: translateX(-50%) scale(1.7);
        opacity: 1;
      }
    }

    @keyframes foreground-clouds-wide-desktop {
      0% {
        transform: translateX(-55%) scale(1.6);
      }
      100% {
        transform: translateX(-50%) scale(2);
        opacity: 1;
      }
    }
  }

  /**
   * Unavailable Screen
   */

  .unavailable-screen {
    position: relative;
    margin-top: -68px;
    min-height: 100vh;
    background: linear-gradient(180deg, #dc4193 0%, #2d77cb 60%);
    pointer-events: auto;
    z-index: 1;
    overflow: hidden;

    .content-wrapper {
      padding-top: 6rem;

      @screen md {
        padding-top: 8rem;
      }
    }

    .main-heading {
      font-size: 40px;

      @screen md {
        font-size: 80px;
        max-width: 800px;
        margin: 0 auto;
      }
    }

    .form-heading {
      margin-top: 80%;
      margin-left: auto;
      margin-right: auto;
      max-width: 500px;
      font-size: 16px;
      font-weight: 300;
      color: #fff;
      text-align: center;

      @screen md {
        margin-top: 480px;
        font-size: 18px;
      }

      &.footer-form-heading {
        margin-top: 3rem;
      }
    }

    .delivery-count-wrapper {
      margin-top: 6rem;
      margin-bottom: 6rem;
      text-align: center;

      @screen md {
        margin-top: 6rem;
        margin-top: 10rem;
      }
    }

    .delivery-count-heading {
      font-size: 32px;
      font-family: var(--font-secondary);
      font-weight: var(--font-weight-semibold);
      line-height: 1;
      color: #fff;
      text-align: center;

      @screen md {
        font-size: 40px;
      }

      @screen lg {
        font-size: 48px;
      }
    }

    .delivery-count-subheading {
      margin-top: 0.5rem;
      font-size: 18px;
      color: #fff;
      text-align: center;

      @screen md {
        font-size: 26px;
      }
    }

    .delivery-count-number {
      display: block;
      margin-top: 1rem;
      font-family: var(--font-secondary);
      font-size: 90px;
      font-weight: 600;
      line-height: 1.3;
      color: #fff;

      @screen md {
        font-size: 150px;
      }

      @screen lg {
        font-size: 225px;
      }

      @screen xl {
        margin-top: 2rem;
        font-size: 300px;
      }
    }

    .learn-more-posts {
      display: flex;
      flex-direction: column;
      gap: 3rem;
      margin-top: 6rem;

      @screen md {
        flex-direction: row;
        margin-top: 8rem;
      }
    }

    .learn-more-post-text {
      font-size: 16px;
      color: #fff;
      font-size: 16px;

      @screen md {
        font-size: 18px;
      }
    }

    .learn-more-post-text {
      margin-top: 1rem;
      font-weight: 300;
    }

    :global(.learn-more-post-text a) {
      color: #fff;
      text-decoration: underline;
    }

    .learn-more-post-image {
      border-radius: 20px;
    }

    .unavailable-footer {
      width: 100%;
      min-height: 100vh;
      margin-top: 8rem;
      background-image: url("/images/check-availability/unavailable/background-mobile.webp");
      background-repeat: no-repeat;
      background-position: top bottom;
      background-size: cover;

      @screen lg {
        min-height: 100dvh;
        margin-top: 12rem;
        background-image: url("/images/check-availability/unavailable/background-desktop.webp");
      }
    }

    .unavailable-droid {
      position: absolute;
      width: 80vw;
      height: auto;
      top: 10vh;
      left: 50%;
      transform: translateX(-50%);
      transition: all 1.8s ease;
      z-index: -1;

      @screen md {
        width: 500px;
        transition: all 1.4s ease;
      }
    }

    .clouds {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-image: url("/images/check-availability/common/background-clouds.webp");
      background-repeat: no-repeat;
      background-position: center top;
      background-size: contain;
      opacity: 0;
      z-index: -2;
      pointer-events: none;
      animation: unavailable-clouds-mobile 2s ease forwards;

      @screen lg {
        animation: unavailable-clouds-desktop 2s ease forwards;
      }
    }

    @keyframes unavailable-clouds-mobile {
      0% {
        transform: scale(2) translateY(60dvh);
      }

      100% {
        transform: scale(1.6) translateY(40dvh);
        opacity: 0.4;
      }
    }

    @keyframes unavailable-clouds-desktop {
      0% {
        transform: scale(1.6) translateY(10dvh);
      }

      100% {
        transform: scale(1.2) translateY(-30dvh);
        opacity: 0.4;
      }
    }
  }
</style>
